import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IAPIState, IInfoBlockItemType } from "shared/types";
import { CreateInfoBlockItemModal as Component } from "shared/components";

import {
  clearActionMeta,
  clearActionPagination,
  createInfoBlockItem,
  dataMetaSelector,
} from "Venues/state";
import { useVenuePageContext } from "Venues/contexts";
import { getInfoBlockItemKey, useClearActionMetaOnUnMount } from "Venues/utils";
import { VENUE_ACTIONS } from "Venues/types";
import { API_ENDPOINTS } from "shared/constants";
import { CreateInfoBlockItemBaseProps, useCreateInfoBlockItem } from "shared/utils";

export const CreateInfoBlockItemModal: React.FC<CreateInfoBlockItemBaseProps> = ({
  type,
  patchInfoBlock,
  hasError,
  handleClose,
  infoBlockApiStatus,
  ...rest
}) => {
  const { venueId } = useVenuePageContext();
  const dispatch = useDispatch();
  const paginationKey = useMemo(() => getInfoBlockItemKey(venueId, type), [venueId, type]);

  useClearActionMetaOnUnMount(VENUE_ACTIONS.createInfoBlockItem);

  const { api: apiMeta, createdItem } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.createInfoBlockItem)
  );

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(clearActionMeta(VENUE_ACTIONS.createInfoBlockItem));

      // delete paginated info block items from store,
      // items have to be fetched again after a new item is added
      dispatch(clearActionPagination(paginationKey));

      // Patch/post infoBlock with new contact
      createdItem && patchInfoBlock && patchInfoBlock(createdItem, type);
    }
  }, [apiMeta?.status, dispatch, createdItem, patchInfoBlock, type, paginationKey]);

  useEffect(() => {
    // Clean up error message on modal unmount
    return () => {
      dispatch(clearActionMeta(VENUE_ACTIONS.createInfoBlockItem));
    };
  }, [dispatch]);

  const handleSubmit = async (item: Partial<IInfoBlockItemType>) => {
    const cleanItem = {
      ...item,
      venue: venueId ? API_ENDPOINTS.VENUE(venueId) : undefined,
    };

    if (venueId) {
      dispatch(createInfoBlockItem({ item: cleanItem, type }));
    }
  };

  const { itemReadbleName, formProps, Form } = useCreateInfoBlockItem({
    type,
    handleClose,
    handleSubmit,
    infoBlockApiStatus,
    hasError: !!apiMeta?.error || hasError,
    createApiStatus: apiMeta?.status,
  });

  return (
    <Component
      type={type}
      handleClose={handleClose}
      itemReadbleName={itemReadbleName}
      Component={Form()}
      componentProps={formProps}
      {...rest}
    />
  );
};
